import { useState, useCallback, useEffect, useRef } from "react";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import { CardMedia } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SaveIcon from "@mui/icons-material/Save";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSIconButton from "views/system-design/DSIconButton";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSPrimaryButton from "views/system-design/DSPrimaryButton";
import DSCard from "views/system-design/DSCard";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import useOnboardingImages from "../hooks/useOnboardingImages";
import useSaveOnboardingImages from "../hooks/useSaveOnboardingImages";
import useUploadOnboardingImages from "../hooks/useUploadOnboardingImage";

const OnboardingSettings = () => {
  const { data, isLoading } = useOnboardingImages();
  const { mutate: saveImages, isPending: isSaving } = useSaveOnboardingImages();
  const { mutateAsync: uploadImages, isPending: isUploading } =
    useUploadOnboardingImages();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (data?.images) {
      setImages(data.images);
    }
  }, [data]);

  const handleAddImage = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files?.length) return;

      const urls = await uploadImages(Array.from(files));
      setImages((prev) => {
        const updated = [...prev, ...urls];
        setHasChanges(true);
        return updated;
      });
      event.target.value = "";
    },
    [uploadImages],
  );

  const handleDelete = useCallback((index: number) => {
    setImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      setHasChanges(true);
      return updated;
    });
  }, []);

  const handleDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    setImages((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(source.index, 1);
      updated.splice(destination.index, 0, moved);
      setHasChanges(true);
      return updated;
    });
  }, []);

  const handleSave = useCallback(() => {
    saveImages(images, {
      onSuccess: () => setHasChanges(false),
    });
  }, [images, saveImages]);

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <DSCircularProgress />
      </DSBox>
    );
  }

  return (
    <DSBox sx={{ p: 3, maxWidth: 1000 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <DSTypography variant="h5">Onboarding Slides</DSTypography>
        <DSBox sx={{ display: "flex", gap: 2 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png,image/jpeg,image/jpg,image/png"
            multiple
            style={{ display: "none" }}
            onChange={handleAddImage}
            disabled={isUploading}
          />
          <DSSecondaryButton
            onClick={() => fileInputRef.current?.click()}
            startIcon={
              isUploading ? (
                <DSCircularProgress size={20} />
              ) : (
                <AddPhotoAlternateIcon />
              )
            }
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Add Images"}
          </DSSecondaryButton>
          <DSPrimaryButton
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? "Saving..." : "Save Order"}
          </DSPrimaryButton>
        </DSBox>
      </DSBox>

      {images.length === 0 ? (
        <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
          No onboarding slides uploaded yet. Click &quot;Add Images&quot; to get
          started.
        </DSTypography>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="onboarding-slides">
            {(provided) => (
              <DSBox
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                {images.map((url, index) => (
                  <Draggable
                    key={`slide-${url}`}
                    draggableId={`slide-${url}`}
                    index={index}
                  >
                    {(dragProvided, snapshot) => (
                      <DSCard
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          boxShadow: snapshot.isDragging ? 6 : 1,
                          bgcolor: snapshot.isDragging
                            ? "action.hover"
                            : "background.paper",
                        }}
                      >
                        <DSBox
                          {...dragProvided.dragHandleProps}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 1,
                            cursor: "grab",
                            color: "text.secondary",
                          }}
                        >
                          <DragIndicatorIcon />
                        </DSBox>
                        <DSBox
                          sx={{
                            width: 36,
                            textAlign: "center",
                            fontWeight: "bold",
                            color: "text.secondary",
                            flexShrink: 0,
                          }}
                        >
                          {index + 1}
                        </DSBox>
                        <CardMedia
                          component="img"
                          image={url}
                          alt={`Slide ${index + 1}`}
                          sx={{
                            width: 200,
                            height: 112,
                            objectFit: "cover",
                            borderRadius: 1,
                            my: 0.5,
                            flexShrink: 0,
                          }}
                        />
                        <DSBox sx={{ flex: 1 }} />
                        <DSIconButton
                          size="small"
                          onClick={() => handleDelete(index)}
                          color="error"
                          sx={{ mr: 1 }}
                        >
                          <DeleteIcon />
                        </DSIconButton>
                      </DSCard>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </DSBox>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </DSBox>
  );
};

export default OnboardingSettings;
